import clsx from 'clsx'
import { Link } from 'react-router-dom'
import { Lock } from '@/assets/icons'
import { Tag } from '@/components/tag/Tag'

export type SideNavItemProps = {
  /** The label text displayed in the nav item */
  label: string
  /** The route to navigate to */
  to: string
  /** Icon component to display (from @/assets/icons) */
  icon?: React.FC<React.SVGProps<SVGSVGElement>>
  /** Whether the item is currently active/selected */
  isActive?: boolean
  /** Whether to show the lock icon (for unauthenticated users) */
  showLock?: boolean
  /** Whether to show the beta tag */
  showBetaTag?: boolean
  /** Click handler for the nav item */
  onClick?: () => void
  /** Whether the item is disabled */
  disabled?: boolean
  /** Additional classes to add to the icon element - Used for home icon padding */
  iconClassName?: string
  /** Visual weight of the item. 'subtle' renders a smaller, muted style for lightweight actions (e.g. "Back to all matters"). Defaults to 'default'. */
  variant?: 'default' | 'subtle'
}

/**
 * A side navigation item component.
 *
 * ## States
 * - **Default**: Neutral text/icon colors
 * - **Hover**: Light background highlight (automatic via Tailwind)
 * - **Focus**: Blue outline ring (automatic via Tailwind)
 * - **Active**: Blue text/icon with light background (via `isActive` prop)
 * - **Disabled**: Reduced opacity, no interactions (via `disabled` prop)
 *
 * The 'subtle' variant is a smaller, permanently-muted style for lightweight actions that are
 * never the active item (e.g. "Back to all matters"), rather than a primary destination.
 *
 * For collapsible groups with sub-items, use SideNavGroup instead.
 */
export function SideNavItem({
  label,
  to,
  icon: Icon,
  isActive = false,
  showLock = false,
  showBetaTag = false,
  onClick,
  disabled = false,
  iconClassName,
  variant = 'default',
}: SideNavItemProps) {
  const isSubtle = variant === 'subtle'
  return (
    <Link
      to={to}
      data-ds-component="SideNavItem"
      onClick={disabled ? (e) => e.preventDefault() : onClick}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : undefined}
      className={clsx(
        // Base styles
        'group flex w-full items-center gap-2 rounded-md leading-5',
        isSubtle ? 'h-9 py-1 pl-2 pr-1 text-xs font-medium' : 'h-10 p-2 text-sm font-medium',
        // Match the trailing badge inset used by Matter Records (including Timeline).
        showBetaTag && !showLock && 'pr-1',
        // Focus styles (keyboard navigation - not subitems)
        'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500',
        // State styles
        isSubtle
          ? 'text-text-tertiary hover:bg-surface-hover hover:text-brand-neutral-700'
          : isActive
          ? 'bg-surface-selected-subtle text-brand-500'
          : 'text-text-secondary hover:bg-surface-hover hover:text-brand-neutral-900 active:bg-surface-hover',
        // Disabled styles
        disabled && 'pointer-events-none opacity-50'
      )}
      aria-current={isActive ? 'page' : undefined}
    >
      {Icon && (
        <Icon
          className={clsx(
            isSubtle ? 'w-4 h-4' : 'w-5 h-5',
            'shrink-0',
            isActive ? 'text-brand-500' : 'text-text-secondary group-hover:text-brand-neutral-900',
            iconClassName
          )}
          aria-hidden="true"
        />
      )}

      <span className={clsx('font-rethink-sans', isActive && 'text-brand-500')}>{label}</span>

      {showLock && <Lock className="ml-auto h-5 w-5 shrink-0 text-brand-neutral-500" aria-hidden="true" />}

      {showBetaTag && !showLock && (
        <span className="ml-auto flex shrink-0 items-center">
          <Tag variant="filled" color="Black" size="2xs">
            beta
          </Tag>
        </span>
      )}
    </Link>
  )
}
